import { useEffect, useRef, useState } from 'react';
import { off, onValue, ref } from 'firebase/database';

import { database } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { getImageGenerationJobs, getImageGenerationRequests } from '../services/imageGenerationService';

function sortByDateDesc(items, key) {
  return [...items].sort((a, b) => {
    const left = a?.[key] ? new Date(a[key]).getTime() : 0;
    const right = b?.[key] ? new Date(b[key]).getTime() : 0;
    return right - left;
  });
}

export function useImageGenerationData() {
  const { firebaseUserId } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!firebaseUserId) {
      setJobs([]);
      setRequests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const jobsRef = ref(database, `users/${firebaseUserId}/generated_images`);
    const requestsRef = ref(database, `users/${firebaseUserId}/generation_requests`);

    const loadFallback = async () => {
      try {
        const [apiJobs, apiRequests] = await Promise.all([
          getImageGenerationJobs(),
          getImageGenerationRequests(),
        ]);
        setJobs(sortByDateDesc(apiJobs || [], 'created_at'));
        setRequests(
          [...(apiRequests || [])].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0))
        );
        setError(null);
      } catch (fallbackError) {
        setError(fallbackError?.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
        initialLoadDone.current = true;
      }
    };

    const handleJobsValue = async (snapshot) => {
      const data = snapshot.val();
      const parsedJobs = data ? Object.values(data) : [];

      if (parsedJobs.length === 0 && !initialLoadDone.current) {
        await loadFallback();
        return;
      }

      setJobs(sortByDateDesc(parsedJobs, 'created_at'));
      setLoading(false);
      setError(null);
      initialLoadDone.current = true;
    };

    const handleRequestsValue = (snapshot) => {
      const data = snapshot.val();
      const parsedRequests = data
        ? Object.entries(data).map(([requestId, value]) => ({ request_id: requestId, ...value }))
        : [];
      setRequests(parsedRequests.sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0)));
    };

    const handleError = async () => {
      await loadFallback();
    };

    onValue(jobsRef, handleJobsValue, handleError);
    onValue(requestsRef, handleRequestsValue, handleError);

    return () => {
      off(jobsRef);
      off(requestsRef);
    };
  }, [firebaseUserId]);

  return {
    jobs,
    requests,
    loading,
    error,
  };
}

export default useImageGenerationData;
